import React, { useState, useEffect, useRef } from 'react';
import content from '../data/content';
import '../styles/MainActivities.css';

function MainActivities() {
  const { overview, teams } = content;
  const [expandedCard, setExpandedCard] = useState(null);
  const [showTimeline, setShowTimeline] = useState(false);
  const timelineItemsRef = useRef([]);
  const teamDetailsRef = useRef(null);

  const teamData = {
    '大会チーム': teams.contest,
    'メタバースチーム': teams.metaverse,
    'VTuberチーム': teams.vtuber
  };

  const toggleCard = (index) => {
    setExpandedCard(expandedCard === index ? null : index);
    // カードが閉じられた時は年表も閉じる
    if (expandedCard === index) {
      setShowTimeline(false);
    }
  };

  // チーム詳細展開時にスクロール（スマホのみ）
  useEffect(() => {
    if (expandedCard !== null && teamDetailsRef.current) {
      // スマホ画面かどうかをチェック（768px以下）
      const isMobile = window.innerWidth <= 768;

      if (isMobile) {
        // 少し遅延を入れて、DOMの更新を待つ
        setTimeout(() => {
          const element = teamDetailsRef.current;
          if (element) {
            const navbarHeight = 60; // ナビゲーションバーの高さ
            const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
            const offsetPosition = elementPosition - navbarHeight - 20; // 余白として20px追加

            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
          }
        }, 100);
      }
    }
  }, [expandedCard]);

  useEffect(() => {
    if (!showTimeline) {
      timelineItemsRef.current = [];
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('timeline-item--active');
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: '-50px'
      }
    );

    timelineItemsRef.current.forEach((item) => {
      if (item) {
        observer.observe(item);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [showTimeline, expandedCard]);

  return (
    <section id="main-activities" className="main-activities-section">
      <div className="main-activities-container">
        <h2>{overview.title}</h2>
        <div className="main-activities-grid">
          {overview.mainActivities.map((activity, index) => (
            <div key={index} className="main-activity-card">
              <h3 className="main-activity-title">{activity.title}</h3>
              <img src={activity.image} alt={activity.title} className="main-activity-image" />
              <div className="main-activity-content">
                <p className="main-activity-description">{activity.description}</p>
                <button 
                  onClick={() => toggleCard(index)}
                  className={`expand-button ${expandedCard === index ? 'expanded' : ''}`}
                  aria-label={expandedCard === index ? "詳細を閉じる" : "詳細を開く"}
                >
                  <span className={`arrow ${expandedCard === index ? 'expanded' : ''}`}></span>
                </button>
              </div>
            </div>
          ))}
        </div>
        
        {expandedCard !== null && (
          <div className="team-details-container" ref={teamDetailsRef}>
            <div className="team-details">
              <div className="team-overview">
                <h4>{overview.mainActivities[expandedCard].title}</h4>
                <p>{teamData[overview.mainActivities[expandedCard].title].overview}</p>
              </div>
              <div className="team-activities">
                <h4>活動履歴</h4>
                <div className="timeline-toggle-container">
                  <button
                    onClick={() => setShowTimeline(!showTimeline)}
                    className={`timeline-toggle-button ${showTimeline ? 'expanded' : ''}`}
                    aria-label={showTimeline ? "年表を閉じる" : "年表を開く"}
                  >
                    <span className={`arrow ${showTimeline ? 'expanded' : ''}`}></span>
                  </button>
                </div>
                {showTimeline && (
                <div className="timeline">
                  {(() => {
                    // 全アイテムを展開してインデックス付きにする
                    const allItemsWithIndex = [];
                    let globalIndex = 0;
                    
                    teamData[overview.mainActivities[expandedCard].title].activities.forEach((yearGroup, yearIndex) => {
                      yearGroup.items.forEach((item, itemIndex) => {
                        const title = typeof item === 'object' ? item.title : item.split('：')[0] || item.substring(0, 30);
                        const description = typeof item === 'object' ? item.description : (item.includes('：') ? item.split('：').slice(1).join('：') : item);
                        const image = typeof item === 'object' ? item.image : null;
                        
                        // 前のアイテムと同じ年かどうかチェック
                        const showYear = globalIndex === 0 || allItemsWithIndex[globalIndex - 1]?.year !== yearGroup.year;
                        
                        allItemsWithIndex.push({
                          key: `${yearIndex}-${itemIndex}`,
                          year: yearGroup.year,
                          title,
                          description,
                          image,
                          showYear
                        });
                        
                        globalIndex++;
                      });
                    });
                    
                    return allItemsWithIndex.map((itemData, index) => (
                      <div
                        key={itemData.key}
                        ref={(el) => (timelineItemsRef.current[index] = el)}
                        className="timeline-item"
                        data-text={itemData.showYear ? itemData.year : ''}
                      >
                        <div className="timeline__content">
                          {itemData.image && <img src={itemData.image} alt={itemData.title} className="timeline__content-image" />}
                          <div className="timeline__content-title">{itemData.title}</div>
                          {itemData.description && <p className="timeline__content-desc">{itemData.description}</p>}
                        </div>
                      </div>
                    ));
                  })()}
                </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export default MainActivities;